import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Send, RefreshCw } from "lucide-react";
import { listMessages, sendMessage, type ChatMessage } from "@/integrations/backend/chat";

interface AdminChatProps {
  clientId: string;
  clientName?: string;
  clientInitials?: string;
}

const AdminChat = ({ clientId, clientName = "Client", clientInitials = "CL" }: AdminChatProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoadRef = useRef(true);

  // Fetch messages function
  const fetchMessages = useCallback(async (isRefresh = false) => {
    if (!clientId) return;
    
    try {
      // Only show loading on initial load
      if (isInitialLoadRef.current) {
        setLoading(true);
      } else if (isRefresh) {
        setRefreshing(true);
      }
      const msgs = await listMessages(clientId);
      setMessages(msgs);
    } catch (error) {
      console.error("Failed to fetch messages", error);
    } finally {
      if (isInitialLoadRef.current) {
        setLoading(false);
        isInitialLoadRef.current = false;
      } else if (isRefresh) {
        setRefreshing(false);
      }
    }
  }, [clientId]);

  // Fetch messages on mount and when clientId changes (initial load only)
  useEffect(() => {
    if (!clientId) return;
    
    // Reset initial load flag when clientId changes
    isInitialLoadRef.current = true;
    fetchMessages();
  }, [clientId, fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    const text = newMessage.trim();
    if (!text || !clientId || sending) return;
    
    try {
      setSending(true);
      // When admin sends, profile_id = client profile ID
      await sendMessage({
        profile_id: clientId,
        content: text,
      });
      setNewMessage("");
      // Refetch messages to get all messages including the one we just sent
      await fetchMessages(false);
    } catch (error) {
      console.error("Failed to send message", error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return "";
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-black font-semibold">
                {clientInitials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-lg text-gray-800">{clientName}</h3>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchMessages(true)}
            disabled={refreshing}
            className="text-gray-600 hover:text-gray-900"
            title="Refresh messages"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Messages Area */}
        <div ref={scrollRef} className="h-[50vh] overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No messages yet. Start the conversation!</div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.is_sender ? "justify-end" : "justify-start"}`}
              >
                {!message.is_sender && (
                  <Avatar className="w-8 h-8 mr-2 flex-shrink-0">
                    {message.sender_avatar_url ? (
                      <AvatarImage src={message.sender_avatar_url} alt={message.sender_name} />
                    ) : (
                      <AvatarFallback className="bg-gray-200 text-gray-700 text-xs">
                        {message.sender_initials}
                      </AvatarFallback>
                    )}
                  </Avatar>
                )}
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    message.is_sender
                      ? "bg-gradient-to-r from-primary to-primary/90 text-black"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                  <p
                    className={`text-xs mt-1 ${
                      message.is_sender ? "text-black/70" : "text-gray-500"
                    }`}
                  >
                    {formatTime(message.created_at)}
                  </p>
                </div>
                {message.is_sender && (
                  <Avatar className="w-8 h-8 ml-2 flex-shrink-0">
                    {message.sender_avatar_url ? (
                      <AvatarImage src={message.sender_avatar_url} alt={message.sender_name} />
                    ) : (
                      <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">
                        {message.sender_initials}
                      </AvatarFallback>
                    )}
                  </Avatar>
                )}
              </div>
            ))
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 border-t bg-white">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending}
                className="border-gray-300 focus:border-primary"
              />
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
              className="bg-gradient-to-r from-primary to-primary/90 text-black hover:opacity-90 rounded-full h-10 w-10 p-0 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminChat;
